'use client';

import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import { getChartData } from '@/api/generated/reports/reports';
import { TimeRangeKind, UserDimension, type GetInteractionAggregatesReqGroupBy } from '@/api/generated/model';
import { QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { getStaticTrendBenchmarks, STATIC_LEVEL_BENCHMARKS, STATIC_ORG_SCORE_BENCHMARKS, type LevelBenchmarkShare, type OrgScoreBenchmarks, type TrendBenchmarkPoint } from '@/domains/pages/ai-proficiency/fixtures/benchmarks';
import { AIFI_PROFICIENCY_MIGRATION_VARIABLES, GROUP_BY_AIFI_PROFICIENCY } from '@/domains/pages/ai-proficiency/utils/aifi-aggregates-api';
import { mapProficiencyMigrationFromAggregates } from '@/domains/pages/ai-proficiency/utils/map-migration';
import { getAifiTimeRangeShortLabel } from '@/domains/pages/ai-proficiency/constants';
import { formatTrendBucketLabel } from '@/domains/pages/shared/trend-query';
import { buildLevelMixChartConfig, buildOrgScoreTrendChartConfig, resolveAiProficiencyAggregateTimeRange } from '@/domains/pages/ai-proficiency/utils/api-payloads';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS as rollingChartQueryOptions, buildAiProficiencyChartTrendQueryKey, resolveAiProficiencyMonthlyTrendQuery, resolveWeeklyFallbackTrendQuery, shouldFallbackToWeeklyRollingTrend } from '@/domains/pages/ai-proficiency/utils/rolling-chart-trend';
import { trimLeadingEmptyChartTrendData } from '@/domains/pages/ai-proficiency/utils/trim-chart-trend-buckets';
import { mapLevelMixFromChartData, mapLevelSnapshots, mergeLevelSnapshotsWithTeams, mapOrgScoreSnapshot, mapOrgScoreTimeFromChartData, type LevelSnapshot, type OrgScoreSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import type { LevelMigrationSummary } from '@/domains/pages/ai-proficiency/fixtures/migration';
import { mapAnalyticsConfiguration } from '@/domains/reports/utils/configuration-mapper';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS, aiProficiencyLadderProficiencyQueryKey, aiProficiencyOrgScoreQueryKey, getAiProficiencyTimeRangeQueryKey } from '@/domains/pages/ai-proficiency/query-options';
import { isQueryCacheMissing } from '@/domains/pages/ai-proficiency/utils/query-loading';
import { useCallback, useMemo } from 'react';
import { useAiProficiencyEffectiveNow, useAiProficiencyStableNow } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-stable-now';
import { useUserDimensionMapping } from '@/domains/pages/shared/use-user-dimension-mapping';
import { humanizeMappedTeamName } from '@/domains/pages/ai-proficiency/utils/segment-display';
import { alignSeriesToBuckets } from '@/domains/pages/shared/chart-axis';
const EMPTY_BUCKETS: string[] = [];
const LADDER_TEAMS_TIMEOUT_MS = 20_000;
export type AiProficiencyPageData = {
  orgScore: OrgScoreSnapshot;
  levels: LevelSnapshot[];
  trendLabels: string[];
  orgScoresOverTime: Array<number | null>;
  levelMixOverTime: Record<string, number[]>;
  levelCountsOverTime: Record<string, number[]>;
  trendBenchmarks: TrendBenchmarkPoint[];
  orgBenchmarks: OrgScoreBenchmarks | null;
  levelBenchmarks: Record<AifiLevel, LevelBenchmarkShare>;
  migration: LevelMigrationSummary;
  migrationTimeRangeLabel: string;
  isLoading: boolean;
  isInitialLoading: boolean;
  isChartsLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};
type UseAiProficiencyPageDataParams = {
  projectId: string | undefined;
};
export function useAiProficiencyPageData({
  projectId
}: UseAiProficiencyPageDataParams): AiProficiencyPageData {
  const stableNow = useAiProficiencyStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useAiProficiencyEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  const aggregateTimeRange = useMemo(() => resolveAiProficiencyAggregateTimeRange(stableNow, effectiveNow), [stableNowKey, effectiveNowKey, stableNow, effectiveNow]);
  const aggregateTimeRangeQueryKey = useMemo(() => getAiProficiencyTimeRangeQueryKey(aggregateTimeRange), [aggregateTimeRange]);
  const monthlyTrendQuery = useMemo(() => resolveAiProficiencyMonthlyTrendQuery(stableNow, effectiveNow), [stableNowKey, effectiveNowKey]);
  const enabled = Boolean(projectId && aggregateTimeRange && isEffectiveNowReady);
  const {
    hasMapping: hasTeamMapping,
    mappedTagKeys: teamMappedTagKeys,
    isLoading: isLoadingTeamMapping
  } = useUserDimensionMapping(UserDimension.team, enabled);
  const teamMappingKey = teamMappedTagKeys.join('|');
  const monthlyChartTrendQueryKey = useMemo(() => buildAiProficiencyChartTrendQueryKey(monthlyTrendQuery), [monthlyTrendQuery]);
  const orgTrendMonthlyPayload = useMemo(() => mapAnalyticsConfiguration(buildOrgScoreTrendChartConfig(monthlyTrendQuery)), [monthlyTrendQuery]);
  const levelMixMonthlyPayload = useMemo(() => mapAnalyticsConfiguration(buildLevelMixChartConfig(monthlyTrendQuery)), [monthlyTrendQuery]);
  const scoreQuery = useQuery({
    queryKey: aiProficiencyOrgScoreQueryKey(projectId!, aggregateTimeRangeQueryKey),
    queryFn: ({
      signal
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: ['aifi_score'],
      calculate_previous: true
    }, {
      signal
    }),
    enabled,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const scoreReady = enabled && scoreQuery.isSuccess;
  const ladderProficiencyQuery = useQuery({
    queryKey: aiProficiencyLadderProficiencyQueryKey(projectId!, aggregateTimeRangeQueryKey),
    queryFn: ({
      signal: signal_0
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: ['n_users'],
      group_by: GROUP_BY_AIFI_PROFICIENCY,
      limit: 100
    }, {
      signal: signal_0
    }),
    enabled,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const ladderTeamsQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-proficiency-ladder-teams', projectId, aggregateTimeRangeQueryKey, teamMappingKey],
    queryFn: ({
      signal: signal_1
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: ['n_users'],
      group_by: GROUP_BY_AIFI_PROFICIENCY,
      additional_group_bys: [{
        kind: 'team'
      } as GetInteractionAggregatesReqGroupBy],
      limit: 100
    }, {
      signal: signal_1,
      timeout: LADDER_TEAMS_TIMEOUT_MS
    }),
    enabled: scoreReady && ladderProficiencyQuery.isSuccess,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const proficiencyMigrationQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-proficiency-tier-migration', projectId, aggregateTimeRangeQueryKey],
    queryFn: ({
      signal: signal_2
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: AIFI_PROFICIENCY_MIGRATION_VARIABLES,
      calculate_previous: false,
      limit: 1
    }, {
      signal: signal_2
    }),
    enabled: scoreReady,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const orgTrendMonthlyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-org-score-trend', projectId, monthlyChartTrendQueryKey],
    queryFn: ({
      signal: signal_3
    }) => getChartData(projectId!, orgTrendMonthlyPayload, {
      signal: signal_3
    }),
    enabled: scoreReady,
    ...rollingChartQueryOptions
  });
  const levelMixMonthlyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-level-mix', projectId, monthlyChartTrendQueryKey],
    queryFn: ({
      signal: signal_4
    }) => getChartData(projectId!, levelMixMonthlyPayload, {
      signal: signal_4
    }),
    enabled: scoreReady,
    ...rollingChartQueryOptions
  });
  const needsWeeklyFallback = useMemo(() => shouldFallbackToWeeklyRollingTrend(orgTrendMonthlyQuery.data?.chart, orgTrendMonthlyQuery.isSuccess), [orgTrendMonthlyQuery.data?.chart, orgTrendMonthlyQuery.isSuccess]);
  const weeklyTrendQuery = useMemo(() => resolveWeeklyFallbackTrendQuery(orgTrendMonthlyQuery.data?.chart, monthlyTrendQuery), [monthlyTrendQuery, orgTrendMonthlyQuery.data?.chart]);
  const weeklyChartTrendQueryKey = useMemo(() => buildAiProficiencyChartTrendQueryKey(weeklyTrendQuery), [weeklyTrendQuery]);
  const orgTrendWeeklyPayload = useMemo(() => mapAnalyticsConfiguration(buildOrgScoreTrendChartConfig(weeklyTrendQuery)), [weeklyTrendQuery]);
  const levelMixWeeklyPayload = useMemo(() => mapAnalyticsConfiguration(buildLevelMixChartConfig(weeklyTrendQuery)), [weeklyTrendQuery]);
  const orgTrendWeeklyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-org-score-trend', projectId, weeklyChartTrendQueryKey],
    queryFn: ({
      signal: signal_5
    }) => getChartData(projectId!, orgTrendWeeklyPayload, {
      signal: signal_5
    }),
    enabled: scoreReady && needsWeeklyFallback,
    ...rollingChartQueryOptions
  });
  const levelMixWeeklyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-level-mix', projectId, weeklyChartTrendQueryKey],
    queryFn: ({
      signal: signal_6
    }) => getChartData(projectId!, levelMixWeeklyPayload, {
      signal: signal_6
    }),
    enabled: scoreReady && needsWeeklyFallback,
    ...rollingChartQueryOptions
  });
  const activeTrendQuery = needsWeeklyFallback ? weeklyTrendQuery : monthlyTrendQuery;
  const orgScore = useMemo(() => scoreQuery.data ? mapOrgScoreSnapshot(scoreQuery.data) : null, [scoreQuery.data]);
  const levels = useMemo(() => {
    if (!ladderProficiencyQuery.data || !orgScore) return [];
    const proficiencyLevels = mapLevelSnapshots(ladderProficiencyQuery.data);
    const merged = mergeLevelSnapshotsWithTeams(proficiencyLevels, ladderTeamsQuery.data);
    if (!hasTeamMapping) return merged;
    return merged.map(level => ({
      ...level,
      teams: level.teams.map(team => ({
        ...team,
        team: humanizeMappedTeamName(team.team, true)
      }))
    }));
  }, [hasTeamMapping, ladderProficiencyQuery.data, ladderTeamsQuery.data, orgScore]);
  const orgTrendFromChart = useMemo(() => {
    const rawChart = needsWeeklyFallback ? orgTrendWeeklyQuery.data?.chart : orgTrendMonthlyQuery.data?.chart;
    if (!rawChart) return null;
    return mapOrgScoreTimeFromChartData(rawChart);
  }, [needsWeeklyFallback, orgTrendMonthlyQuery.data?.chart, orgTrendWeeklyQuery.data?.chart]);
  const levelMixFromChart = useMemo(() => {
    const rawChart_0 = needsWeeklyFallback ? levelMixWeeklyQuery.data?.chart : levelMixMonthlyQuery.data?.chart;
    if (!rawChart_0) return null;
    return mapLevelMixFromChartData(rawChart_0);
  }, [levelMixMonthlyQuery.data?.chart, levelMixWeeklyQuery.data?.chart, needsWeeklyFallback]);
  const trimmedChartTrend = useMemo(() => {
    const orgBuckets = orgTrendFromChart?.buckets ?? EMPTY_BUCKETS;
    const levelMixBuckets = levelMixFromChart?.buckets ?? EMPTY_BUCKETS;
    const canonicalBuckets = orgBuckets.length > 0 ? orgBuckets : levelMixBuckets;
    const orgScores = alignSeriesToBuckets(canonicalBuckets, orgBuckets, orgTrendFromChart?.scores ?? []);
    if (canonicalBuckets.length === 0) {
      return null;
    }
    if (!levelMixFromChart) {
      return {
        startIndex: 0,
        buckets: canonicalBuckets,
        mix: {},
        counts: {},
        orgScores
      };
    }
    const alignedMix = Object.fromEntries(Object.entries(levelMixFromChart.mix).map(([level_0, values]) => [level_0, alignSeriesToBuckets(canonicalBuckets, levelMixBuckets, values).map(value => value ?? 0)]));
    const alignedCounts = Object.fromEntries(Object.entries(levelMixFromChart.counts).map(([level_1, values_0]) => [level_1, alignSeriesToBuckets(canonicalBuckets, levelMixBuckets, values_0).map(value_0 => value_0 ?? 0)]));
    return trimLeadingEmptyChartTrendData({
      buckets: canonicalBuckets,
      mix: alignedMix,
      counts: alignedCounts,
      orgScores
    });
  }, [levelMixFromChart, orgTrendFromChart]);
  const trendBuckets = trimmedChartTrend?.buckets ?? EMPTY_BUCKETS;
  const trendLabels = useMemo(() => trendBuckets.map((bucket, index) => formatTrendBucketLabel(bucket, activeTrendQuery.granularity, activeTrendQuery.bucketKind, index === trendBuckets.length - 1)), [activeTrendQuery.bucketKind, activeTrendQuery.granularity, trendBuckets]);
  const orgScoresOverTime = trimmedChartTrend?.orgScores ?? [];
  const levelMixOverTime = trimmedChartTrend?.mix ?? {};
  const levelCountsOverTime = trimmedChartTrend?.counts ?? {};
  const trendBenchmarks = useMemo(() => getStaticTrendBenchmarks(trendLabels.length), [trendLabels.length]);
  const migration = useMemo(() => {
    if (!proficiencyMigrationQuery.isSuccess || !proficiencyMigrationQuery.data) {
      return {
        movedUp: 0,
        slippedBack: 0,
        stayed: 0
      };
    }
    return mapProficiencyMigrationFromAggregates(proficiencyMigrationQuery.data);
  }, [proficiencyMigrationQuery.isSuccess, proficiencyMigrationQuery.data]);
  const isInitialLoading = !isEffectiveNowReady || enabled && (isQueryCacheMissing(scoreQuery) || isQueryCacheMissing(ladderProficiencyQuery) || isLoadingTeamMapping);
  const isChartsLoading = scoreReady && (isQueryCacheMissing(orgTrendMonthlyQuery) || isQueryCacheMissing(levelMixMonthlyQuery) || needsWeeklyFallback && (isQueryCacheMissing(orgTrendWeeklyQuery) || isQueryCacheMissing(levelMixWeeklyQuery)) || isQueryCacheMissing(proficiencyMigrationQuery));
  const isLoading = isInitialLoading;
  const isError = scoreQuery.isError || ladderProficiencyQuery.isError || ladderTeamsQuery.isError || orgTrendMonthlyQuery.isError || levelMixMonthlyQuery.isError || needsWeeklyFallback && (orgTrendWeeklyQuery.isError || levelMixWeeklyQuery.isError);
  const error = scoreQuery.error ?? ladderProficiencyQuery.error ?? ladderTeamsQuery.error ?? orgTrendMonthlyQuery.error ?? levelMixMonthlyQuery.error ?? (needsWeeklyFallback ? orgTrendWeeklyQuery.error ?? levelMixWeeklyQuery.error : null);
  const refetch = useCallback(() => {
    void scoreQuery.refetch();
    void ladderProficiencyQuery.refetch();
    void ladderTeamsQuery.refetch();
    void proficiencyMigrationQuery.refetch();
    void orgTrendMonthlyQuery.refetch();
    void levelMixMonthlyQuery.refetch();
    if (needsWeeklyFallback) {
      void orgTrendWeeklyQuery.refetch();
      void levelMixWeeklyQuery.refetch();
    }
  }, [ladderProficiencyQuery.refetch, ladderTeamsQuery.refetch, levelMixMonthlyQuery.refetch, levelMixWeeklyQuery.refetch, needsWeeklyFallback, orgTrendMonthlyQuery.refetch, orgTrendWeeklyQuery.refetch, proficiencyMigrationQuery.refetch, scoreQuery.refetch]);
  return {
    orgScore: orgScore ?? {
      score: null,
      scorePrev: null,
      delta: null,
      totalUsers: 0
    },
    levels,
    trendLabels,
    orgScoresOverTime,
    levelMixOverTime,
    levelCountsOverTime,
    trendBenchmarks,
    orgBenchmarks: STATIC_ORG_SCORE_BENCHMARKS,
    levelBenchmarks: STATIC_LEVEL_BENCHMARKS,
    migration,
    migrationTimeRangeLabel: getAifiTimeRangeShortLabel(TimeRangeKind['30_days']),
    isLoading,
    isInitialLoading,
    isChartsLoading,
    isError,
    error,
    refetch
  };
}